"use client";

import React from "react";
import { motion } from "framer-motion";
import { Globe, ChartBar as BarChart2, Target, CircleAlert as AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
interface MetricItem {
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const metrics: MetricItem[] = [{
  title: "Country, Language and Directory Segmentation",
  description: "Tracking organic impressions, clicks, sessions, and rankings filtered by target country, language version, and specific subfolder or ccTLD property in Google Search Console and analytics.",
  icon: Globe
}, {
  title: "Priority Keyword Positions per Market",
  description: "Monitoring priority keywords in each market's native search engine results rather than pooling global rankings, ensuring search intent matches local terminology.",
  icon: Target
}, {
  title: "Conversions, Revenue and Enquiry Volume",
  description: "Evaluating assisted and direct conversions, qualified enquiry volume, and localized ecommerce revenue per territory to assess the commercial return of international traffic.",
  icon: BarChart2
}];
export default function MeasuringInternationalSEOPerformance() {
  return <section id="measuringinternationalseoperformance" data-nav="light" className="bg-background text-foreground py-20 md:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }} className="space-y-4 max-w-3xl mb-14">
          <Badge variant="outline" className="text-primary border-primary/20 bg-primary/5">
            Measurement &amp; Reporting
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground font-serif">
            Measuring International SEO Performance
          </h2>
          <p className="text-muted-foreground text-lg sm:text-xl leading-relaxed pt-2">
            When I evaluate international search campaigns, I segment analytics and Google Search Console by country, language, and directory. Relying on blended global numbers is misleading: aggregate visibility routinely conceals rapid gains in an emerging regional market or masks quiet declines in an established core territory.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {metrics.map((metric, i) => {
          const Icon = metric.icon;
          return <motion.div key={metric.title} data-index={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1,
            ease: [0.16, 1, 0.3, 1]
          }}>
                <Card className="h-full border border-border bg-card shadow-xs hover:border-primary/30 transition-colors">
                  <CardHeader className="space-y-3 pb-3">
                    <div className="size-10 rounded-md bg-secondary flex items-center justify-center text-primary">
                      <Icon className="size-5" />
                    </div>
                    <CardTitle className="text-xl font-semibold tracking-tight text-card-foreground">
                      {metric.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed text-sm">
                      {metric.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Dashboards and Non-converting Markets note */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.25,
        ease: [0.16, 1, 0.3, 1]
      }} className="mb-8 p-6 sm:p-8 rounded-xl border border-border bg-card shadow-xs space-y-4">
          <h3 className="text-xl font-semibold text-foreground font-serif">
            Per-Country Dashboards and Diagnosing Non-Converting Traffic
          </h3>
          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
            I build dedicated reporting dashboards per country. Tracking organic sessions alongside assisted and direct conversions makes it straightforward to see which regions deliver commercial value. If a market attracts organic visits but fails to generate conversions or enquiries, I investigate localized content, user experience, localized pricing, payment methods, and operational factors such as shipping or delivery terms.
          </p>
        </motion.div>

        {/* Search Console Reporting Note */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }}>
          <Alert className="border-border bg-secondary/50 p-6 sm:p-7">
            <AlertCircle className="size-5 text-primary" />
            <AlertTitle className="text-base font-semibold text-foreground">
              Google Search Console for Reporting and Diagnosis
            </AlertTitle>
            <AlertDescription className="text-muted-foreground text-sm leading-relaxed mt-2">
              Google Search Console is a reporting and diagnostic tool. There is no country-targeting setting to rely on; accurate measurement uses country filters alongside directory-level or separate property configurations to monitor indexation and search queries for each regional audience.
            </AlertDescription>
          </Alert>
        </motion.div>
      </div>
    </section>;
}